/**
 * stockData denormalized projection cache reader (entity + blob).
 * Not inventory write truth (ADR-0016) — qty truth is InventoryMovement + lot projection.
 * Prefer getStockData / projectStockFromLots for operational qty; this is load/UI denorm only.
 */

import { getEntityStockData } from '../infrastructure/storage/entityAggregateStore';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { readRootDocFieldFromMergedBlob } from './businessStateSync';

/** @type {Map<string, object>} */
const stockDataReadCache = new Map();

export function readStockDataFromMergedBlob(businessId) {
  return readRootDocFieldFromMergedBlob(businessId, 'stockData', getCachedStockDataRead, {});
}

export async function readStockDataFromBestSource(businessId) {
  if (!businessId) return {};

  if (isEntityStoreNoKvBlobEnabled()) {
    try {
      const fromEntity = await getEntityStockData(businessId);
      if (fromEntity && Object.keys(fromEntity).length > 0) {
        return fromEntity;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[stockDataReadSource] entity read failed:', err?.message || err);
      }
    }
    return readStockDataFromMergedBlob(businessId);
  }

  try {
    const fromEntity = await getEntityStockData(businessId);
    if (fromEntity && Object.keys(fromEntity).length > 0) {
      return fromEntity;
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[stockDataReadSource] entity read failed:', err?.message || err);
    }
  }

  const fromBlob = readStockDataFromMergedBlob(businessId);
  if (Object.keys(fromBlob).length > 0) return fromBlob;

  try {
    const fromEntity = await getEntityStockData(businessId);
    if (fromEntity && Object.keys(fromEntity).length > 0) return fromEntity;
  } catch {
    /* ignore */
  }

  return fromBlob;
}

export async function refreshStockDataReadCache(businessId, preloaded) {
  const stockData =
    preloaded !== undefined
      ? preloaded && typeof preloaded === 'object'
        ? preloaded
        : {}
      : await readStockDataFromBestSource(businessId);
  if (businessId) {
    stockDataReadCache.set(String(businessId), stockData);
  }
  return stockData;
}

export function getCachedStockDataRead(businessId) {
  if (!businessId) return null;
  const cached = stockDataReadCache.get(String(businessId));
  return cached != null ? cached : null;
}

export function clearStockDataReadCache(businessId) {
  if (businessId) {
    stockDataReadCache.delete(String(businessId));
  } else {
    stockDataReadCache.clear();
  }
}

export function seedStockDataReadCache(businessId, stockData) {
  if (!businessId || stockData === undefined) return;
  stockDataReadCache.set(
    String(businessId),
    stockData && typeof stockData === 'object' ? stockData : {}
  );
}

export function readStockDataSyncForBusiness(businessId) {
  if (!businessId) return {};
  const cached = getCachedStockDataRead(businessId);
  if (cached) return cached;
  if (isEntityStoreNoKvBlobEnabled()) return {};
  return readStockDataFromMergedBlob(businessId);
}
